import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import Footer from '@/components/Footer';
import { 
  FileText, CheckCircle, ArrowRight, Clock, 
  AlertTriangle, Target, Calendar, Download, ChevronRight, Globe
} from 'lucide-react';
import { format } from 'date-fns';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Legend
} from 'recharts';
import { paymentsAPI } from '@/lib/api';
import { SafeResponsiveContainer } from '@/components/SafeChart';

// Helper function to get color based on percentage
// 90-100: green, 70-89: orange, <70: red
const getPercentColor = (pct) => {
  if (pct >= 90) return { stroke: '#22c55e', text: 'text-emerald-600', bg: 'bg-emerald-600' };
  if (pct >= 70) return { stroke: '#f59e0b', text: 'text-amber-600', bg: 'bg-amber-500' };
  return { stroke: '#ef4444', text: 'text-red-600', bg: 'bg-red-500' };
};

// Blue color for project completion (not RAG)
const projectCompletionColor = { stroke: '#3b82f6', text: 'text-blue-600', bg: 'bg-blue-500' };

// Reusable Progress Circle component
const ProgressCircle = ({ percent, size = 96, strokeWidth = 8, label, sublabel, labelBelow = false, isProjectCompletion = false }) => {
  const color = isProjectCompletion ? projectCompletionColor : getPercentColor(percent);
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (percent / 100) * circumference;
  
  return (
    <div className="flex-shrink-0 text-center">
      <div className="relative inline-block" style={{ width: size, height: size }}>
        <svg className="transform -rotate-90" style={{ width: size, height: size }}>
          <circle 
            cx={size/2} cy={size/2} r={radius} 
            stroke="#e2e8f0" 
            strokeWidth={strokeWidth} 
            fill="none" 
          />
          <circle 
            cx={size/2} cy={size/2} r={radius} 
            stroke={color.stroke}
            strokeWidth={strokeWidth} 
            fill="none" 
            strokeDasharray={circumference} 
            strokeDashoffset={offset}
            strokeLinecap="round"
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className={`text-xl font-bold ${color.text}`}>{percent}%</span>
          {sublabel && !labelBelow && <span className="text-xs text-slate-400">{sublabel}</span>}
        </div>
      </div>
      {(label || (sublabel && labelBelow)) && (
        <div className="text-xs text-slate-500 mt-1">{labelBelow ? sublabel : label}</div>
      )}
    </div>
  );
};

// Sprint Progress Bar component (full width version)
const SprintProgressBar = ({ current, total, label }) => {
  const percent = (current / total) * 100;
  return (
    <div className="w-full">
      <div className="flex justify-between text-xs text-slate-500 mb-1">
        <span>{label || `Sprint ${current} of ${total}`}</span>
        <span>{Math.round(percent)}%</span>
      </div>
      <div className="w-full bg-slate-200 rounded-full h-3">
        <div 
          className="bg-emerald-500 h-3 rounded-full transition-all" 
          style={{ width: `${percent}%` }}
        ></div>
      </div>
    </div>
  );
};

// Sample S-Curve data - actual curve shape (slow start, steep middle, flatten)
const scurveData = [
  { week: 'W1', pv: 8000, ev: 6000, ac: 7000 },
  { week: 'W2', pv: 18000, ev: 14000, ac: 16000 },
  { week: 'W3', pv: 35000, ev: 28000, ac: 32000 },
  { week: 'W4', pv: 58000, ev: 48000, ac: 52000 },
  { week: 'W5', pv: 78000, ev: 68000, ac: 72000 },
  { week: 'W6', pv: 92000, ev: 82000, ac: 85000 },
  { week: 'W7', pv: 100000, ev: 90000, ac: 92000 },
];

const LandingPage = () => {
  const [activeTab, setActiveTab] = useState('predictive');
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const navigate = useNavigate();

  // Handle pricing button click - redirect to signup or checkout
  const handlePricingClick = async (tier) => {
    // Check if user is logged in
    const token = localStorage.getItem('token');
    
    if (!token) {
      // Not logged in - redirect to signup with plan info
      navigate(`/signup?plan=${tier}&billing=${billingCycle}`);
      return;
    }
    
    // User is logged in - create checkout session
    setCheckoutLoading(tier);
    try {
      const packageId = `${tier}_${billingCycle}`;
      const response = await paymentsAPI.createCheckout(packageId);
      window.location.href = response.data.url;
    } catch (err) {
      console.error('Checkout error:', err);
      // If checkout fails, redirect to settings
      navigate('/settings');
    } finally {
      setCheckoutLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-slate-200 bg-white sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileText className="h-6 w-6 text-slate-900" />
            <span className="text-lg font-semibold text-slate-900 tracking-tight">ProjectStatusReport</span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/login">
              <Button variant="ghost" className="text-slate-600 hover:text-slate-900" data-testid="login-btn">
                Log In
              </Button>
            </Link>
            <Link to="/signup">
              <Button className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="signup-btn">
                Start Free Trial
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-white pt-16 pb-8">
        <div className="max-w-6xl mx-auto px-6">
          {/* Headline */}
          <div className="text-center mb-12">
            <h1 className="text-5xl md:text-6xl font-bold text-slate-900 tracking-tight mb-4">
              Status Reports.<br />Done in 5 Minutes.
            </h1>
            <p className="text-xl text-slate-600 mb-2">
              Communication is the key to project success.
            </p>
            <p className="text-sm text-slate-500 mb-8">
              This is what your stakeholders will see:
            </p>
          </div>

          {/* Report Type Tabs */}
          <div className="flex justify-center gap-1 mb-6">
            <button 
              onClick={() => setActiveTab('predictive')}
              className={`px-6 py-2 text-sm font-medium transition-all rounded-md ${
                activeTab === 'predictive' 
                  ? 'bg-slate-900 text-white' 
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              Predictive
            </button>
            <button 
              onClick={() => setActiveTab('agile')}
              className={`px-6 py-2 text-sm font-medium transition-all rounded-md ${
                activeTab === 'agile' 
                  ? 'bg-slate-900 text-white' 
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              Agile / Scrum
            </button>
          </div>

          {/* Report Preview */}
          <div className="max-w-5xl mx-auto">
            <div className="border border-slate-200 rounded-lg shadow-sm overflow-hidden bg-white">
              {activeTab === 'predictive' ? <PredictiveReport /> : <AgileReport />}
            </div>
          </div>

          {/* CTA */}
          <div className="text-center mt-10">
            <Link to="/signup">
              <Button size="lg" className="bg-slate-900 hover:bg-slate-800 text-white px-8 py-6 text-base" data-testid="hero-cta-btn">
                Create Your First Report <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
            <p className="text-slate-500 text-sm mt-3">14 days free · No credit card required</p>
          </div>
        </div>
      </section>

      {/* Authority Line */}
      <section className="py-12 border-t border-slate-100">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <p className="text-slate-600 text-lg">
            Designed by project management experts with over 30 years of real-world experience.
          </p>
        </div>
      </section>

      {/* 3-Step Process with Mini UI Mockups */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-slate-900 text-center mb-16">How It Works</h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            {/* Step 1: Setup */}
            <div className="flex flex-col group">
              <div className="text-5xl font-bold text-slate-300 mb-4 text-center">1</div>
              <div className="bg-white rounded-lg border border-slate-200 p-4 shadow-sm h-60 transition-all duration-200 group-hover:shadow-lg group-hover:scale-[1.02] group-hover:border-slate-300 overflow-hidden relative">
                <div className="text-left">
                  <div className="text-xs text-slate-400 uppercase tracking-wide mb-3">New Project</div>
                  <div className="space-y-2">
                    <div>
                      <div className="text-xs text-slate-500 mb-1">Project Name</div>
                      <div className="h-7 bg-slate-100 rounded border border-slate-200 px-2 flex items-center text-xs text-slate-600">Website Redesign</div>
                    </div>
                    <div>
                      <div className="text-xs text-slate-500 mb-1">Methodology</div>
                      <div className="h-7 bg-slate-100 rounded border border-slate-200 px-2 flex items-center justify-between text-xs text-slate-600">
                        <span>Predictive</span>
                        <svg className="w-3 h-3 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-slate-500 mb-1">Project Manager</div>
                      <div className="h-7 bg-slate-100 rounded border border-slate-200 px-2 flex items-center text-xs text-slate-600">Tracy Smith</div>
                    </div>
                    <div>
                      <div className="text-xs text-slate-500 mb-1">Budget</div>
                      <div className="h-7 bg-slate-100 rounded border border-slate-200 px-2 flex items-center text-xs text-slate-600">$1,200,000</div>
                    </div>
                  </div>
                </div>
                {/* Fade gradient overlay */}
                <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-white to-transparent pointer-events-none"></div>
              </div>
              <div className="mt-4 text-center">
                <h3 className="text-xl font-semibold text-slate-900 mb-2">Setup</h3>
                <p className="text-slate-600 text-sm leading-relaxed">One-time setup with all your project details: milestones, dates, budget, team. Stored for the entire project duration.</p>
              </div>
            </div>

            {/* Step 2: Update */}
            <div className="flex flex-col group">
              <div className="text-5xl font-bold text-slate-300 mb-4 text-center">2</div>
              <div className="bg-white rounded-lg border border-slate-200 p-4 shadow-sm h-60 transition-all duration-200 group-hover:shadow-lg group-hover:scale-[1.02] group-hover:border-slate-300 overflow-hidden relative">
                <div className="text-left">
                  <div className="text-xs text-slate-400 uppercase tracking-wide mb-3">Weekly Update</div>
                  <div className="space-y-2">
                    <div>
                      <div className="text-xs text-slate-500 mb-1">Status</div>
                      <div className="flex gap-1 flex-nowrap">
                        <div className="h-5 px-1.5 bg-emerald-100 text-emerald-700 rounded text-[10px] flex items-center font-medium border-2 border-emerald-500 whitespace-nowrap">On Track</div>
                        <div className="h-5 px-1.5 bg-slate-100 text-slate-500 rounded text-[10px] flex items-center whitespace-nowrap">At Risk</div>
                        <div className="h-5 px-1.5 bg-slate-100 text-slate-500 rounded text-[10px] flex items-center whitespace-nowrap">Off Track</div>
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-slate-500 mb-1">Accomplishments</div>
                      <div className="h-10 bg-slate-100 rounded border border-slate-200 p-2 text-xs text-slate-600">✓ Homepage design complete...</div>
                    </div>
                    <div>
                      <div className="text-xs text-slate-500 mb-1">Blockers</div>
                      <div className="h-7 bg-red-50 rounded border border-red-200 px-2 flex items-center text-xs text-red-700 whitespace-nowrap overflow-hidden">⚠ Waiting on brand assets</div>
                    </div>
                    <div>
                      <div className="text-xs text-slate-500 mb-1">Progress</div>
                      <div className="h-2 bg-slate-200 rounded-full"><div className="h-2 bg-emerald-500 rounded-full w-3/5"></div></div>
                    </div>
                  </div>
                </div>
                {/* Fade gradient overlay */}
                <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-white to-transparent pointer-events-none"></div>
              </div>
              <div className="mt-4 text-center">
                <h3 className="text-xl font-semibold text-slate-900 mb-2">Update</h3>
                <p className="text-slate-600 text-sm leading-relaxed">Each week update what's changed: victories, status, blockers, risks. Set milestones, track progress, note decisions. 5 minutes.</p>
              </div>
            </div>

            {/* Step 3: Export & Share */}
            <div className="flex flex-col group">
              <div className="text-5xl font-bold text-slate-300 mb-4 text-center">3</div>
              <div className="bg-white rounded-lg border border-slate-200 p-4 shadow-sm h-60 transition-all duration-200 group-hover:shadow-lg group-hover:scale-[1.02] group-hover:border-slate-300 overflow-hidden relative">
                <div className="text-left">
                  <div className="text-xs text-slate-400 uppercase tracking-wide mb-3">Share Report</div>
                  <div className="flex flex-col items-center py-2">
                    <div className="flex gap-4 mb-4">
                      <div className="w-14 h-16 bg-slate-100 rounded border border-slate-200 flex flex-col items-center justify-center relative">
                        <FileText className="w-6 h-6 text-slate-400" />
                        <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-red-500 rounded flex items-center justify-center">
                          <span className="text-white text-[10px] font-bold">PDF</span>
                        </div>
                      </div>
                      <div className="w-14 h-16 bg-slate-100 rounded border border-slate-200 flex flex-col items-center justify-center relative">
                        <Globe className="w-6 h-6 text-slate-400" />
                        <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-blue-500 rounded flex items-center justify-center">
                          <span className="text-white text-[10px] font-bold">URL</span>
                        </div>
                      </div>
                    </div>
                    <div className="w-full h-8 bg-slate-900 rounded flex items-center justify-center gap-2 text-white text-sm font-medium mb-2">
                      <Download className="w-4 h-4" />
                      Export PDF
                    </div>
                    <div className="w-full h-8 bg-blue-600 rounded flex items-center justify-center gap-2 text-white text-sm font-medium">
                      <Globe className="w-4 h-4" />
                      Share URL
                    </div>
                  </div>
                </div>
                {/* Fade gradient overlay */}
                <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-white to-transparent pointer-events-none"></div>
              </div>
              <div className="mt-4 text-center">
                <h3 className="text-xl font-semibold text-slate-900 mb-2">Share</h3>
                <p className="text-slate-600 text-sm leading-relaxed">Export to PDF or share via URL. Send your report and you're done. Communication is key to success. We make it easy.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features - Simple */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-slate-900 text-center mb-4">Everything You Need</h2>
          <p className="text-slate-600 text-center mb-16">Simple tools for the weekly grind</p>
          
          <div className="grid md:grid-cols-2 gap-x-16 gap-y-10">
            <div className="flex gap-4">
              <Clock className="h-6 w-6 text-slate-400 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-slate-900 mb-1">5-Minute Reports</h3>
                <p className="text-slate-600 text-sm">Setup once. Each week, just update what changed.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <Target className="h-6 w-6 text-slate-400 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-slate-900 mb-1">Milestone Tracking</h3>
                <p className="text-slate-600 text-sm">Target dates, actual dates, visual progress.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <AlertTriangle className="h-6 w-6 text-slate-400 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-slate-900 mb-1">Risk Management</h3>
                <p className="text-slate-600 text-sm">Log risks, track status, show you're on top of it.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <Download className="h-6 w-6 text-slate-400 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-slate-900 mb-1">Clean PDF Export</h3>
                <p className="text-slate-600 text-sm">Professional output. No formatting nightmares.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 bg-slate-50" id="pricing">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-slate-900 text-center mb-4">Simple Pricing</h2>
          <p className="text-slate-600 text-center mb-8">What's your time worth?</p>
          
          {/* Billing Toggle */}
          <div className="flex justify-center mb-10">
            <div className="inline-flex items-center gap-3 bg-white border border-slate-200 p-1.5 rounded-full shadow-sm">
              <button
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                  billingCycle === 'monthly' 
                    ? 'bg-slate-900 text-white shadow-sm' 
                    : 'text-slate-600 hover:text-slate-900'
                }`}
                onClick={() => setBillingCycle('monthly')}
              >
                Monthly
              </button>
              <button
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                  billingCycle === 'yearly' 
                    ? 'bg-slate-900 text-white shadow-sm' 
                    : 'text-slate-600 hover:text-slate-900'
                }`}
                onClick={() => setBillingCycle('yearly')}
              >
                Annual
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  billingCycle === 'yearly' 
                    ? 'bg-green-500 text-white' 
                    : 'bg-green-100 text-green-700'
                }`}>
                  2 months free
                </span>
              </button>
            </div>
          </div>
          
          <div className="max-w-md mx-auto">
            {/* Single Professional Tier */}
            <div className="bg-slate-900 text-white rounded-lg p-8 transition-all duration-200 hover:shadow-xl hover:scale-[1.02] relative overflow-hidden">
              <div className="absolute top-4 right-4 bg-blue-500 text-white text-xs font-medium px-2 py-1 rounded">
                All Features Included
              </div>
              <h3 className="font-semibold text-lg mb-1">Professional</h3>
              <div className="mb-2">
                <span className="text-4xl font-bold">
                  ${billingCycle === 'yearly' ? '290' : '29'}
                </span>
                <span className="text-slate-400">/{billingCycle === 'yearly' ? 'year' : 'month'}</span>
              </div>
              {billingCycle === 'yearly' && (
                <p className="text-sm text-green-400 mb-4">
                  $24/month · Save $58/year
                </p>
              )}
              {billingCycle === 'monthly' && <div className="mb-4" />}
              <ul className="space-y-3 mb-8 text-sm text-slate-300">
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-400" />
                  Unlimited projects
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-400" />
                  Full report history
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-400" />
                  PDF export (no watermark)
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-400" />
                  Shareable report links
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-400" />
                  All charts & metrics
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-400" />
                  Bulk PDF export
                </li>
              </ul>
              <Button 
                className="w-full bg-white text-slate-900 hover:bg-slate-100"
                onClick={() => handlePricingClick('professional')}
                disabled={checkoutLoading === 'professional'}
              >
                {checkoutLoading === 'professional' ? 'Loading...' : 'Get Started'}
              </Button>
            </div>
          </div>

          <p className="text-center text-slate-500 text-sm mt-8">
            Start with a 14-day free trial. No credit card required.
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-white border-t border-slate-100">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-slate-900 mb-4">
            Your Fridays Just Got Better
          </h2>
          <p className="text-slate-600 mb-8">
            Stop dreading status report day.
          </p>
          <Link to="/signup">
            <Button size="lg" className="bg-slate-900 hover:bg-slate-800 text-white px-8 py-6 text-base">
              Start Free Trial <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-200 py-8 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-slate-400" />
              <span className="font-semibold text-slate-600">ProjectStatusReport</span>
            </div>
            <div className="flex items-center gap-4 text-sm text-slate-500">
              <Link to="/privacy" className="hover:text-slate-700">Privacy Policy</Link>
              <span>·</span>
              <Link to="/terms" className="hover:text-slate-700">Terms of Service</Link>
              <span>·</span>
              <a href="mailto:cs@instructing.com" className="hover:text-slate-700">Contact</a>
            </div>
            <p className="text-slate-500 text-sm">© 2026 Instructing.com LLC. All rights reserved. &nbsp;V1.27.27</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

// ============================================
// PREDICTIVE REPORT COMPONENT
// ============================================
const PredictiveReport = () => {
  const todayFormatted = format(new Date(), 'MMMM d, yyyy');
  
  return (
    <div>
      {/* Header */}
      <div className="bg-slate-800 text-white p-6">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-xl font-semibold mb-1">Website Redesign Project</h2>
            <p className="text-slate-400 text-sm">Weekly Status Report</p>
          </div>
          <div className="text-right text-sm">
            <div className="text-slate-400">Report Date</div>
            <div className="font-medium">{todayFormatted}</div>
          </div>
        </div>
        
        <div className="mt-4 flex items-center gap-4">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-amber-400"></div>
            <span className="font-medium text-amber-400 text-sm">AT RISK</span>
          </div>
          <div className="h-4 w-px bg-slate-600"></div>
          <p className="text-slate-300 text-sm">
            Design phase complete. Schedule slipping due to vendor delays.
          </p>
        </div>
      </div>

      {/* Metrics Bar */}
      <div className="bg-slate-50 border-b border-slate-200 px-6 py-4">
        <div className="flex items-center justify-between text-center">
          <div>
            <div className="text-2xl font-bold text-blue-600">65%</div>
            <div className="text-xs text-slate-500 uppercase">Complete</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-amber-600">.87</div>
            <div className="text-xs text-slate-500 uppercase">SPI</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-emerald-600">.90</div>
            <div className="text-xs text-slate-500 uppercase">CPI</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-slate-700">3 of 5</div>
            <div className="text-xs text-slate-500 uppercase">Milestones</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-amber-600">2</div>
            <div className="text-xs text-slate-500 uppercase">Risks</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-red-600">1</div>
            <div className="text-xs text-slate-500 uppercase">Blocker</div>
          </div>
        </div>
      </div>

      {/* S-Curve Chart */}
      <div className="p-6 border-b border-slate-200">
        <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Project Performance</h3>
        <SafeResponsiveContainer height={192}>
          <LineChart data={scurveData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="week" fontSize={11} stroke="#94a3b8" />
            <YAxis fontSize={11} stroke="#94a3b8" tickFormatter={(v) => `$${(v/1000).toFixed(0)}K`} />
            <Legend wrapperStyle={{ fontSize: '11px' }} />
            <Line type="monotone" dataKey="pv" stroke="#3b82f6" strokeWidth={2} name="Planned Value" dot={false} />
            <Line type="monotone" dataKey="ev" stroke="#22c55e" strokeWidth={2} name="Earned Value" dot={false} />
            <Line type="monotone" dataKey="ac" stroke="#f97316" strokeWidth={2} name="Actual Cost" dot={false} />
          </LineChart>
        </SafeResponsiveContainer>
      </div>

      {/* Content */}
      <div className="p-6">
        <div className="grid md:grid-cols-2 gap-6">
          {/* Left Column */}
          <div className="space-y-6">
            {/* Accomplishments */}
            <div>
              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3 flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-500" />
                Accomplishments This Week
              </h3>
              <div className="space-y-2">
                <div className="p-3 bg-emerald-50 rounded text-sm">
                  <p className="text-slate-700">Completed homepage and product page designs</p>
                  <p className="text-slate-500 text-xs mt-1">Sarah Chen</p>
                </div>
                <div className="p-3 bg-emerald-50 rounded text-sm">
                  <p className="text-slate-700">Stakeholder approval on final mockups</p>
                  <p className="text-slate-500 text-xs mt-1">Mike Johnson</p>
                </div>
                <div className="p-3 bg-emerald-50 rounded text-sm">
                  <p className="text-slate-700">Dev environment setup complete</p>
                  <p className="text-slate-500 text-xs mt-1">Dev Team</p>
                </div>
              </div>
            </div>

            {/* Upcoming */}
            <div>
              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3 flex items-center gap-2">
                <Calendar className="w-4 h-4 text-blue-500" />
                Planned for Next Week
              </h3>
              <div className="space-y-2">
                <div className="flex items-center justify-between p-3 bg-blue-50 rounded text-sm">
                  <span className="text-slate-700">Begin front-end development</span>
                  <span className="text-slate-500 text-xs">Jan 13</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-blue-50 rounded text-sm">
                  <span className="text-slate-700">CMS integration planning</span>
                  <span className="text-slate-500 text-xs">Jan 14</span>
                </div>
              </div>
            </div>

            {/* Blocker */}
            <div>
              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-red-500" />
                Blockers
              </h3>
              <div className="p-3 bg-red-50 border border-red-200 rounded">
                <p className="font-medium text-red-800 text-sm">Waiting on brand assets from marketing</p>
                <p className="text-red-600 text-xs mt-1">Blocked: 3 days · Lisa Park</p>
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {/* Milestones */}
            <div>
              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3 flex items-center gap-2">
                <Target className="w-4 h-4 text-blue-500" />
                Milestones
              </h3>
              <div className="space-y-2">
                <MilestoneRow name="Project Kickoff" date="Dec 1" done />
                <MilestoneRow name="Requirements Complete" date="Dec 15" done />
                <MilestoneRow name="Design Complete" date="Jan 3" done />
                <MilestoneRow name="Development Complete" date="Feb 28" current />
                <MilestoneRow name="Launch" date="Mar 15" />
              </div>
            </div>

            {/* Risks */}
            <div>
              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-500" />
                Active Risks
              </h3>
              <div className="space-y-2">
                <div className="p-3 bg-amber-50 border border-amber-200 rounded">
                  <p className="text-slate-700 text-sm">Third-party API documentation unclear</p>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="px-2 py-0.5 bg-amber-200 text-amber-800 text-xs font-medium rounded">MED</span>
                    <span className="px-2 py-0.5 bg-red-200 text-red-800 text-xs font-medium rounded">HIGH IMPACT</span>
                  </div>
                </div>
                <div className="p-3 bg-amber-50 border border-amber-200 rounded">
                  <p className="text-slate-700 text-sm">Key developer PTO Feb 10-17</p>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="px-2 py-0.5 bg-green-200 text-green-800 text-xs font-medium rounded">LOW</span>
                    <span className="px-2 py-0.5 bg-amber-200 text-amber-800 text-xs font-medium rounded">MED IMPACT</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-slate-50 border-t border-slate-200 px-6 py-3 flex items-center justify-between text-xs text-slate-500">
        <span><strong>PM:</strong> Tracy Smith</span>
        <span><strong>Sponsor:</strong> Jennifer Walsh</span>
        <span>Generated by ProjectStatusReport.com</span>
      </div>
    </div>
  );
};

// ============================================
// AGILE REPORT COMPONENT (ORIGINAL - DO NOT MODIFY)
// ============================================
const AgileReport = () => (
  <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
    {/* Header */}
    <div className="bg-gradient-to-r from-emerald-700 via-teal-700 to-emerald-800 text-white p-6">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-xl font-bold mb-1">Mobile App v2.0</h2>
          <p className="text-emerald-200 text-sm">Sprint 8 Status Report</p>
        </div>
        <div className="text-right">
          <div className="text-xs text-emerald-300">Sprint Dates</div>
          <div className="font-semibold">Dec 16 - Jan 10</div>
        </div>
      </div>
      
      <div className="mt-4 flex items-center gap-4">
        <div className="flex items-center gap-2">
          <CheckCircle className="w-5 h-5 text-emerald-400" />
          <span className="font-bold text-emerald-300">SPRINT GOAL: TRENDING</span>
        </div>
        <div className="h-6 w-px bg-emerald-600"></div>
        <div className="flex-1 text-emerald-100 text-sm">
          User authentication module on track. Core login complete, 2FA in progress.
        </div>
      </div>
    </div>

    {/* Sprint Metrics - Full Width Progress Bar */}
    <div className="bg-slate-50 border-b px-6 py-4">
      <SprintProgressBar current={8} total={12} label="Sprint 8 of 12 Completion" />
    </div>

    {/* Progress Section - User Stories + Release + Velocity */}
    <div className="p-6 border-b">
      <div className="flex items-center gap-6">
        {/* User Stories Completed Progress Bar */}
        <div className="flex-1">
          <div className="flex justify-between text-xs text-slate-500 mb-1">
            <span>User Stories Completed</span>
            <span>45 of 52</span>
          </div>
          <div className="w-full bg-slate-200 rounded-full h-3">
            <div className="bg-emerald-500 h-3 rounded-full" style={{ width: '87%' }}></div>
          </div>
        </div>
        
        {/* 78% Release - blue circle for project completion */}
        <ProgressCircle percent={78} size={64} strokeWidth={5} sublabel="Release" labelBelow={true} isProjectCompletion={true} />
        
        {/* Avg Velocity */}
        <div className="text-center">
          <div className="text-2xl font-bold text-purple-600">48</div>
          <div className="text-xs text-slate-500">Avg Velocity</div>
        </div>
      </div>
    </div>

    {/* Content */}
    <div className="p-6">
      <div className="grid md:grid-cols-2 gap-6">
        {/* Left - Completed Stories */}
        <div className="space-y-5">
          <div>
            <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2 flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-emerald-500" />
              Completed This Week
            </h3>
            <div className="space-y-1.5 ml-4">
              <StoryRow name="User login with OAuth" points={13} />
              <StoryRow name="Password reset flow" points={8} />
              <StoryRow name="Session management" points={8} />
              <StoryRow name="Profile settings UI" points={8} />
              <StoryRow name="Security audit fixes" points={8} />
            </div>
          </div>

          <div>
            <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2 flex items-center gap-2">
              <Clock className="w-4 h-4 text-blue-500" />
              In Progress
            </h3>
            <div className="space-y-1.5 ml-4">
              <InProgressRow name="Two-factor authentication" points={5} pct={60} />
              <InProgressRow name="Remember me functionality" points={2} pct={80} />
            </div>
          </div>

          <div>
            <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-red-500" />
              Blockers
            </h3>
            <div className="space-y-2 ml-4">
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="font-medium text-red-800 text-sm">Vendor API for 2FA not responding</p>
                <p className="text-red-600 text-xs mt-1">Blocked: 2 days • Escalated to vendor support</p>
              </div>
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="font-medium text-amber-800 text-sm">CI/CD pipeline slow (15+ min builds)</p>
                <p className="text-amber-600 text-xs mt-1">Being addressed by DevOps this week</p>
              </div>
            </div>
          </div>
        </div>

        {/* Right */}
        <div className="space-y-5">
          <div>
            <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2 flex items-center gap-2">
              <ChevronRight className="w-4 h-4 text-blue-500" />
              Sprint 9 Preview
            </h3>
            <div className="ml-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="font-medium text-blue-800 text-sm mb-2">Goal: Payment integration</p>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between text-slate-600">
                  <span>Stripe integration</span>
                  <span className="text-blue-600 font-medium">13 pts</span>
                </div>
                <div className="flex justify-between text-slate-600">
                  <span>Subscription management</span>
                  <span className="text-blue-600 font-medium">8 pts</span>
                </div>
                <div className="flex justify-between text-slate-600">
                  <span>Invoice generation</span>
                  <span className="text-blue-600 font-medium">5 pts</span>
                </div>
                <div className="flex justify-between border-t border-blue-200 pt-1 mt-1 font-medium">
                  <span>Total Planned</span>
                  <span className="text-blue-700">26 pts</span>
                </div>
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Release Progress</h3>
            <div className="ml-4 p-3 bg-slate-50 rounded-lg">
              <div className="flex justify-between text-sm mb-2">
                <span className="text-slate-600">Completed</span>
                <span className="font-medium text-emerald-600">248 / 320 pts</span>
              </div>
              <div className="w-full bg-slate-200 rounded-full h-3">
                <div className="bg-blue-500 h-3 rounded-full" style={{ width: '78%' }}></div>
              </div>
              <p className="text-center text-xs text-slate-500 mt-2">78% Complete • ~2 sprints remaining</p>
            </div>
          </div>

          <div>
            <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Project Notes</h3>
            <div className="space-y-1.5 ml-4">
              <div className="p-2 bg-emerald-50 rounded-lg">
                <span className="text-xs font-medium text-emerald-600">WORKING WELL</span>
                <p className="text-xs text-slate-700">Pair programming on complex auth logic saving time</p>
              </div>
              <div className="p-2 bg-amber-50 rounded-lg">
                <span className="text-xs font-medium text-amber-600">NEEDS ATTENTION</span>
                <p className="text-xs text-slate-700">Too many ad-hoc meetings disrupting flow</p>
              </div>
              <div className="p-2 bg-blue-50 rounded-lg">
                <span className="text-xs font-medium text-blue-600">UPCOMING</span>
                <p className="text-xs text-slate-700">Security review scheduled for Jan 15</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    {/* Footer */}
    <div className="bg-slate-100 px-6 py-3 flex items-center justify-between text-xs text-slate-500">
      <span><strong>Scrum Master:</strong> Tracy Smith</span>
      <span><strong>Product Owner:</strong> Dana Chen</span>
      <span>Generated by ProjectStatusReport.com</span>
    </div>
  </div>
);

// ============================================
// HELPER COMPONENTS
// ============================================
const MilestoneRow = ({ name, date, done, current }) => (
  <div className={`flex items-center justify-between p-2 rounded text-sm ${current ? 'bg-blue-50 border border-blue-200' : 'bg-blue-50'}`}>
    <div className="flex items-center gap-2">
      {done ? (
        <CheckCircle className="w-4 h-4 text-emerald-500" />
      ) : (
        <div className={`w-4 h-4 rounded-full border-2 ${current ? 'border-blue-500' : 'border-slate-300'}`}></div>
      )}
      <span className={done ? 'text-slate-500' : current ? 'text-slate-700 font-medium' : 'text-slate-500'}>{name}</span>
    </div>
    <span className={`text-xs ${done ? 'text-emerald-600' : current ? 'text-blue-600' : 'text-slate-400'}`}>
      {date}{done && ' ✓'}
    </span>
  </div>
);

const StoryRow = ({ name, points }) => (
  <div className="flex items-center justify-between p-2 bg-emerald-50 rounded-lg text-sm">
    <span className="text-slate-700">{name}</span>
    <span className="text-emerald-600 font-medium">{points} pts</span>
  </div>
);

const InProgressRow = ({ name, points, pct }) => (
  <div className="p-2 bg-blue-50 rounded-lg text-sm">
    <div className="flex items-center justify-between mb-1">
      <span className="text-slate-700">{name}</span>
      <span className="text-blue-600 font-medium">{points} pts</span>
    </div>
    <div className="w-full bg-blue-200 rounded-full h-1.5">
      <div className="bg-blue-500 h-1.5 rounded-full" style={{ width: `${pct}%` }}></div>
    </div>
    <div className="text-right text-xs text-blue-500 mt-0.5">{pct}%</div>
  </div>
);

export default LandingPage;
